import { useEffect, useRef, useState } from 'react';

/**
 * Returns a ref and a boolean that becomes `true` once the element enters
 * the viewport (and stays `true` forever — one-shot).
 *
 * Useful for deferring lazy-loaded components until they're about to be visible.
 *
 * @example
 * const [ref, isVisible] = useInViewport({ rootMargin: '200px' });
 *
 * <div ref={ref}>
 *   {isVisible && (
 *     <Suspense>
 *       <HeavyComponent />
 *     </Suspense>
 *   )}
 * </div>
 */
export function useInViewport({ rootMargin = '0px' } = {}) {
	const ref = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ rootMargin },
		);

		observer.observe(el);
		return () => observer.disconnect();
	}, [rootMargin]);

	return [ref, isVisible] as const;
}
